import { Parser } from '@liveperson/expression-evaluator';
import { PolicyConditionOperator } from '../../../enums';
import {
  removeEnclosingQuotes,
  andOperatorRegEx,
  functionWithSpacesRegEx,
  inOperatorRegEx,
  notOperatorRegEx,
  operatorRegEx,
  orOperatorRegEx,
} from '../../../util';
import { checkValueType } from '../../Editor/util';

export class ConditionNotParsableError extends Error { }

export enum PolicyConnector {
  AND = ' and ',
}

export enum PolicyValueType {
  LIST = 'LIST',
  BOOLEAN = 'BOOLEAN',
  NUMBER = 'NUMBER',
  STRING = 'STRING',
  ATTRIBUTE = 'ATTRIBUTE',
  CHANNEL = 'CHANNEL',
}

const mapOperatorToStr: { [key in PolicyConditionOperator]: string } = {
  [PolicyConditionOperator.EQUAL]: '==',
  [PolicyConditionOperator.NOT_EQUAL]: '!=',
  [PolicyConditionOperator.LESS_THAN]: '<',
  [PolicyConditionOperator.LESS_THAN_OR_EQUAL]: '<=',
  [PolicyConditionOperator.GREATER_THAN]: '>',
  [PolicyConditionOperator.GREATER_THAN_OR_EQUAL]: '>=',
  [PolicyConditionOperator.IN]: 'in',
};

const mapStrToOperator: { [key: string]: PolicyConditionOperator } = {
  '==': PolicyConditionOperator.EQUAL,
  '!=': PolicyConditionOperator.NOT_EQUAL,
  '<=': PolicyConditionOperator.LESS_THAN_OR_EQUAL,
  '<': PolicyConditionOperator.LESS_THAN,
  '>=': PolicyConditionOperator.GREATER_THAN_OR_EQUAL,
  '>': PolicyConditionOperator.GREATER_THAN,
  in: PolicyConditionOperator.IN,
};

const inferValueTypeFromV2Value = (value: string): PolicyValueType => {
  try {
    const parsedValue: any = JSON.parse(value);

    if (Array.isArray(parsedValue)) {
      return PolicyValueType.LIST;
    }

    if (typeof parsedValue === 'boolean') {
      return PolicyValueType.BOOLEAN;
    }
  } catch (err) {
    // parsing error is expected
  }
  if (checkValueType(value) === 'channel') {
    return PolicyValueType.CHANNEL;
  }
  if (/^'(.*?)'$/.test(value)) {
    return PolicyValueType.STRING;
  }

  if (Number.isFinite(Number.parseFloat(value))) {
    return PolicyValueType.NUMBER;
  }

  return PolicyValueType.ATTRIBUTE;
};

const unconvertValue = (value: any): string => {
  const valueType = checkValueType(value);
  if (valueType === 'attribute') {
    return (`${value}`).replace(/^{{/, '').replace(/}}$/, '');
  }
  if (valueType === 'channel') {
    return `'${value.replace(/^%%/, '').replace(/%%$/, '')}'`;
  }
  if (valueType === 'list') {
    return JSON.stringify(value);
  }
  if (valueType === 'string') {
    // remove wrapping single quotes then escape inner single quotes
    const escapedString = removeEnclosingQuotes(value).replace(/['"]/g, '\\$&');
    return `'${escapedString}'`;
  }
  return (`${value}`);
};

const convertValue = (value: string) => {
  const valueType: PolicyValueType = inferValueTypeFromV2Value(value);

  switch (valueType) {
    case PolicyValueType.LIST:
    case PolicyValueType.BOOLEAN:
      return JSON.parse(value);
    case PolicyValueType.STRING:
      if (/^(')(.*?)(')$/.test(value)) {
        return value.substr(1).slice(0, -1).replace(/\\/g, '');
      }
      break;
    case PolicyValueType.ATTRIBUTE:
      return `{{${value}}}`;
    case PolicyValueType.CHANNEL:
      return `%%${value}%%`;
    case PolicyValueType.NUMBER:
      return Number.parseFloat(value);
    default:
      throw new ConditionNotParsableError('Unknown value type');
  }

  throw new ConditionNotParsableError('Unknown value type');
};

interface PolicyConditionV1 {
  property: string;
  type: string;
  value: string;
}

interface TimeBasedPolicyCondition {
  fnName: string;
  fnArguments: Array<string>;
}

/**
 *
 * input:
 * conditions: [
 *  {property: custom.test type: EQUAL value: {{custom.test2}}},
 *  {property: custom.foo type: EQUAL value: 'bar'
 * ]
 *
 * output:
 * condition: "custom.test == custom.test2 and custom.foo == 'bar'"
 *
 *
 * @param policyV1
 * @returns
 */
const convertV1PolicyEditorUiToV2 = (conditions: Array<PolicyConditionV1>): string => {
  const conditionsV2Strings: Array<string> = [];

  conditions.forEach((condition: PolicyConditionV1) => {
    const { property, type: operator, value } = condition;
    if (property) {
      conditionsV2Strings.push(
        `${property} ${mapOperatorToStr[operator as PolicyConditionOperator]} ${unconvertValue(value)}`,
      );
    }
  });

  return conditionsV2Strings.join(PolicyConnector.AND);
};

// returns null if valid
const validateExpressionTokens = (statement: string): Array<string> | null => {
  // output of error messages
  const output: Array<string> = [];

  const expr = Parser.parse(statement);
  // unsupported API (.tokens)
  (expr as any).tokens.forEach((token: any) => {
    if (token.type === 'IOP2' && (token.value === 'not' || token.value === 'or')) {
      output.push(`expression contains logical operator: ${token.value}`);
    } else if (token.type === 'IEXPR') {
      const msg = 'contains sub-expression';
      output.push(msg);
    }
  });

  if (!output.length) {
    return null;
  }

  return output;
};

/**
 * Converts a single condition from v2 format (string) to v1 format (object)
 *
 * @param statement - is a single condition like "custom.foo == 'bar"
 * @returns "custom.foo == 'bar" --> {property: custom.foo, type: EQUALS, value: 'bar'}
 */
const parseV1ConditionStatement = (statement: string): PolicyConditionV1 => {
  const validationRes = validateExpressionTokens(statement);
  if (validationRes) {
    throw new Error(validationRes.join(','));
  }

  const res = statement.match(operatorRegEx);
  const operatorStr = (res as RegExpMatchArray)[0];

  if (!operatorStr) {
    throw new Error('missing operator');
  }

  const [propertyStr, valueStr] = statement.split(operatorStr);
  const property = propertyStr.trim();
  const trimmedValue = valueStr.trim();

  if (!(property && trimmedValue)) {
    throw new Error('missing property or value');
  }

  if (operatorRegEx.test(property)
    || operatorRegEx.test(trimmedValue)) {
    throw new Error('space or math operator in value or property');
  }
  const type: PolicyConditionOperator = mapStrToOperator[operatorStr.trim()];
  const value: any = convertValue(trimmedValue);
  return { property, type, value };
};

/**
 * convertV2ConditionToV1Ui - convert v1 condition string to condition array
 * that the Policy Editor UI can consume
 *
 * example input:
 * condition: "custom.test == [1,2,3] and custom.foo == 'bar"
 *
 * output:
 * conditions: [{
 *  property: 'custom.test'
 *  type: EQUAL
 *  value: [1,2,3]
 * },
 * {
 *  property: 'custom.foo'
 *  type: EQUAL
 *  value: 'bar'
 * }]
 *
 * convertV2ConditionToV1Ui - only simple conditions
 * @param condition - string
 * @returns Array<PolicyConditionV1>
 * @throws ConditionNotParsableError
 */
const convertV2ConditionToV1Ui = (condition: string): Array<PolicyConditionV1> => {
  const conditionsArray: Array<PolicyConditionV1> = [];

  if (condition.length === 0 || condition.trim() === '') {
    return [{ property: '', type: 'EQUAL', value: '' }];
  }

  try {
    const conditionStrs = condition.split(andOperatorRegEx); // split on 'and'

    conditionStrs.forEach((conditionStr: string) => {
      const res = parseV1ConditionStatement(conditionStr); // statement = "row" in UI
      conditionsArray.push(res);
    });
  } catch (err) {
    throw new ConditionNotParsableError(`Cannot parse condition: ${(err as Error).message}`);
  }
  return conditionsArray;
};

/**
 * convertV2ConditionToV1UiWithTimeBased - simple and time-based conditions
 * @param condition
 * @returns
 */
const convertV2ConditionToV1UiWithTimeBased = (
  condition: string,
): Array<PolicyConditionV1 | TimeBasedPolicyCondition> => {
  const conditionsArray: Array<PolicyConditionV1 | TimeBasedPolicyCondition> = [];
  try {
    // anything we show in UI has to be separated by 'AND'
    const conditionStrs = condition.split(andOperatorRegEx);

    conditionStrs.forEach((conditionStr: string) => {
      if (functionWithSpacesRegEx.test(conditionStr)) {
        const match = functionWithSpacesRegEx.exec(conditionStr);

        const fnName = match?.[1].trim();
        const fnArguments = match?.[2].split(',').map((it: string) => it.trim());

        if (fnName?.length && fnArguments?.length) {
          conditionsArray.push({
            fnName,
            fnArguments,
          });
        }
      } else {
        const res = parseV1ConditionStatement(conditionStr);
        conditionsArray.push(res);
      }
    });

    return conditionsArray;
  } catch (err) {
    throw new ConditionNotParsableError('Cannot parse condition');
  }
};

const convertV1PolicyToV2 = (policyV1: any) => {
  const { exception, conditions, ...otherProps } = policyV1;
  const conditionStringV2 = convertV1PolicyEditorUiToV2(conditions);
  const policyV2 = { ...otherProps, condition: conditionStringV2 };
  return policyV2;
};

/**
 * convertV2PolicyToV1
 * @param policyV2
 * @returns any
 * @throws ConditionNotParsableError
 */
const convertV2PolicyToV1 = (policyV2: any): any => {
  try {
    if (!policyV2) {
      return undefined;
    }
    const { condition, ...otherProps } = policyV2;
    const conditionArrayV1 = convertV2ConditionToV1Ui(condition);
    const policyV1 = { ...otherProps, conditions: conditionArrayV1 || condition };
    return policyV1;
  } catch (err) {
    throw new ConditionNotParsableError('Cannot parse condition');
  }
};

const formatV2Condition = (condition: string): string => {
  const formattedCondition = condition.split(andOperatorRegEx).join(' and ')
    .split(orOperatorRegEx).join(' or ')
    .split(notOperatorRegEx)
    .join(' not ')
    .split(inOperatorRegEx)
    .join(' in ');
  return formattedCondition;
};

// public methods
export {
  convertV1PolicyToV2,
  convertV2PolicyToV1,
  inferValueTypeFromV2Value,
  convertV2ConditionToV1Ui,
  convertV1PolicyEditorUiToV2,
  convertV2ConditionToV1UiWithTimeBased,
  formatV2Condition,
  parseV1ConditionStatement,
};
